import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, X, Check, AlertCircle, Loader2, ArrowRight, Star, Users, Target,
  Trophy, BookOpen, Sparkles, Globe, Send
} from 'lucide-react';
import jsPDF from 'jspdf';

type FormData = {
  firstName: string;
  email: string;
  businessName: string;
  currentStage: string;
  biggestChallenge: string;
};

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const STAGES = [
  'BizInception (0-10 customers, <R150K)',
  'Full-Scale Launch (10-100 customers, R150K-R10M)',
  'Known Brand (100-1K customers, R10M-R100M)',
  'Full Operation (1K-10K customers, R100M-R500M)',
  'Corporatising (10K+ customers, R500M-R1B)',
];

const initialFormData: FormData = {
  firstName: '', email: '', businessName: '', currentStage: '', biggestChallenge: '',
};

const SectionDivider = () => (
  <div className="w-full h-0.5 my-8" style={{ background: 'linear-gradient(90deg, transparent, #6B5E2B, transparent)' }} />
);

const CoverPage = () => (
  <div className="relative overflow-hidden rounded-lg" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #2C3E50 50%, #1a1a2e 100%)' }}>
    <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 20% 50%, #6B5E2B 0%, transparent 70%)' }} />
    <div className="relative px-12 py-24 text-center">
      <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#6B5E2B]/30 text-[#6B5E2B] text-sm">
        <Sparkles size={16} /> Chrysalinks Group
      </div>
      <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
        10x Your Revenue:<br /><span className="text-[#6B5E2B]">The Lifestyle Entrepreneur's Playbook</span>
      </h1>
      <p className="text-xl text-slate-300 mb-8" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
        7 Laws to Transform Your Business from Side Hustle to Full-Time Income
      </p>
      <div className="w-24 h-1 bg-[#6B5E2B] mx-auto rounded-full" />
    </div>
  </div>
);

const WelcomeSection = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 bg-white rounded-lg border border-slate-200">
    <div className="max-w-3xl mx-auto">
      <blockquote className="text-lg italic text-[#6B5E2B] border-l-4 border-[#6B5E2B] pl-6 mb-6" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
        &ldquo;Helping lifestyle entrepreneurs 10x their revenues and customer bases.&rdquo;
      </blockquote>
      <p className="text-slate-700 mb-4" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
        This playbook is built for <strong>lifestyle entrepreneurs</strong> &mdash; not unicorn founders chasing billion-dollar exits,
        but real business owners building real businesses that fund their ideal lives.
      </p>
      <p className="text-slate-700" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
        Every section is <strong>action-oriented</strong>, not theoretical. Read it, apply it, and watch your revenue grow.
      </p>
    </div>
  </motion.div>
);

const GrowthStagesTable = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 bg-white rounded-lg border border-slate-200">
    <h2 className="text-2xl font-bold text-[#2C3E50] mb-6" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
      The 5 Stages of Entrepreneurial Growth
    </h2>
    <p className="text-slate-600 mb-6" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>Where are you right now?</p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#6B5E2B] text-white">
            <th className="px-4 py-3 text-left">Stage</th>
            <th className="px-4 py-3 text-left">Name</th>
            <th className="px-4 py-3 text-left">Customers</th>
            <th className="px-4 py-3 text-left">Revenue</th>
            <th className="px-4 py-3 text-left">Focus</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {[
            ['1', 'BizInception', '0-10', '<R150K', 'Quit your day job'],
            ['2', 'Full-Scale Launch', '10-100', 'R150K-R10M', 'Product/Market Fit'],
            ['3', 'Known Brand', '100-1K', 'R10M-R100M', 'Client Monetization'],
            ['4', 'Full Operation', '1K-10K', 'R100M-R500M', 'Business Efficiency'],
            ['5', 'Corporatising', '10K+', 'R500M-R1B', 'Expansion/Scale'],
          ].map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-slate-700" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="mt-6 p-4 bg-[#6B5E2B]/5 rounded-lg border border-[#6B5E2B]/20">
      <p className="text-sm text-slate-600" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
        <strong>Self-Assessment:</strong> Identify your current stage. The fastest path to growth is mastering your current stage
        while preparing for the next one.
      </p>
    </div>
  </motion.div>
);

const SEVEN_LAWS = [
  { num: 1, title: 'Goals & Plans', desc: 'Set your Freedom Number \u2014 the exact monthly revenue needed to fund your ideal lifestyle.', tip: 'Write down your Freedom Number today and reference it every morning.' },
  { num: 2, title: 'Education, Skills & Traits', desc: 'Identify and overcome skill gaps that hold your business back from the next stage.', tip: 'Pick one skill gap per month and dedicate 15 minutes daily to mastering it.' },
  { num: 3, title: 'Wellness (KPIs)', desc: 'Track your business health with objective metrics, not just revenue.', tip: 'Monitor 3 key metrics weekly: cash flow, customer satisfaction, and lead conversion rate.' },
  { num: 4, title: 'Execution', desc: 'The "10-a-Day Mantra" \u2014 consistent daily action beats sporadic heroic effort.', tip: 'Commit to 10 sales conversations every single day, no excuses.' },
  { num: 5, title: 'Obstacle Overcoming', desc: 'Translate frustrations into solutions by reframing problems as opportunities.', tip: 'When stuck, write down the obstacle and three possible solutions in 5 minutes.' },
  { num: 6, title: 'Performance Tracking', desc: 'Measure daily and weekly metrics to know what is working and what is not.', tip: 'Review your weekly numbers every Friday and adjust your strategy for Monday.' },
  { num: 7, title: 'Guidance & Oversight', desc: 'Strategic leadership means knowing when to work IN your business vs ON your business.', tip: 'Schedule 1 hour weekly for strategic thinking, away from daily operations.' },
];

const SevenLawsSection = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 bg-white rounded-lg border border-slate-200">
    <h2 className="text-2xl font-bold text-[#2C3E50] mb-6" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
      The 7 Laws of Success
    </h2>
    <div className="grid gap-4">
      {SEVEN_LAWS.map((law) => (
        <div key={law.num} className="p-4 rounded-lg border border-slate-200 hover:border-[#6B5E2B]/30 transition-colors">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#6B5E2B] text-white flex items-center justify-center font-bold text-lg">
              {law.num}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#2C3E50] mb-1" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>{law.title}</h3>
              <p className="text-slate-600 mb-2" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>{law.desc}</p>
              <div className="p-3 bg-[#6B5E2B]/5 rounded border border-[#6B5E2B]/20">
                <p className="text-sm text-[#6B5E2B]" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
                  <strong>Action Tip:</strong> {law.tip}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

const HotlistSection = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 bg-white rounded-lg border border-slate-200">
    <h2 className="text-2xl font-bold text-[#2C3E50] mb-6" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
      The Hotlist 100 Prospecting System
    </h2>
    <div className="grid md:grid-cols-3 gap-4 mb-6">
      {[
        { icon: Users, label: '15 Conversations', desc: 'Daily outreach to warm prospects from your curated list' },
        { icon: Target, label: '5 Qualified', desc: 'Meaningful conversations with genuine interest' },
        { icon: Trophy, label: '3 Sales', desc: 'Convert qualified leads into paying customers' },
      ].map((item, i) => (
        <div key={i} className="p-4 text-center rounded-lg border border-slate-200">
          <item.icon className="w-8 h-8 mx-auto mb-2 text-[#6B5E2B]" />
          <h3 className="font-bold text-[#2C3E50]" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>{item.label}</h3>
          <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>{item.desc}</p>
        </div>
      ))}
    </div>
    <p className="text-slate-700 mb-4" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
      Build a curated list of 100 warm prospects &mdash; people you know, have met, or have a connection with.
      This is your <strong>Hotlist 100</strong>.
    </p>
    <div className="p-4 bg-[#6B5E2B]/5 rounded-lg border border-[#6B5E2B]/20">
      <p className="text-sm text-[#6B5E2B]" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
        <strong>Start today:</strong> List your first 10 prospects. Reach out to them this week.
      </p>
    </div>
  </motion.div>
);

const TenADaySection = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 bg-white rounded-lg border border-slate-200">
    <h2 className="text-2xl font-bold text-[#2C3E50] mb-6" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
      The 10-a-Day Mantra
    </h2>
    <div className="flex items-start gap-4 mb-6">
      <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#6B5E2B] flex items-center justify-center">
        <span className="text-2xl font-bold text-white">10</span>
      </div>
      <div>
        <p className="text-slate-700 mb-2" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
          Mandatory <strong>10 sales conversations daily</strong>. Not optional. Not negotiable.
        </p>
        <p className="text-slate-600" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
          Outreach = planting seeds. Conversations = watering seeds. Both are essential for growth.
        </p>
      </div>
    </div>
    <div className="p-4 bg-[#6B5E2B]/5 rounded-lg border border-[#6B5E2B]/20">
      <p className="text-sm text-[#6B5E2B]" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
        <strong>Action:</strong> Use a daily outreach tracker. Log your first 10 outreach messages today.
      </p>
    </div>
  </motion.div>
);

const TrustSection = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 bg-white rounded-lg border border-slate-200">
    <h2 className="text-2xl font-bold text-[#2C3E50] mb-6" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
      Trust Builders &amp; Social Proof
    </h2>
    <div className="grid md:grid-cols-2 gap-4">
      {[
        { icon: Globe, title: 'Professional Email', desc: '@yourdomain.com \u2014 looks legitimate, builds trust instantly' },
        { icon: Globe, title: 'Your 1-Pager Website', desc: 'FREE via BizBuilder \u2014 your 24/7 shop window' },
        { icon: Star, title: 'Testimonials', desc: 'Ask every happy customer for a review. Display them prominently.' },
        { icon: Target, title: 'Google Business Profile', desc: 'Local SEO setup \u2014 get found by customers searching near you' },
      ].map((item, i) => (
        <div key={i} className="p-4 rounded-lg border border-slate-200 hover:border-[#6B5E2B]/30 transition-colors">
          <div className="flex items-center gap-3 mb-2">
            <item.icon className="w-5 h-5 text-[#6B5E2B]" />
            <h3 className="font-bold text-[#2C3E50]" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>{item.title}</h3>
          </div>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>{item.desc}</p>
        </div>
      ))}
    </div>
  </motion.div>
);

const NextStepsSection = ({ onDownload, onOpenForm }: { onDownload: () => void; onOpenForm: () => void }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 bg-white rounded-lg border border-slate-200">
    <h2 className="text-2xl font-bold text-[#2C3E50] mb-6" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
      Next Steps
    </h2>
    <div className="grid md:grid-cols-3 gap-4 mb-6">
      {[
        { step: '01', title: 'Self-Assessment', desc: 'Identify your current growth stage and plan your next move' },
        { step: '02', title: 'Join BizBuilder', desc: 'R197/mo self-paced program with full toolset access' },
        { step: '03', title: 'Book Discovery Call', desc: 'For partner-delivered programs and personalized guidance' },
      ].map((item, i) => (
        <div key={i} className="p-4 rounded-lg border border-slate-200 text-center">
          <div className="w-10 h-10 rounded-full bg-[#6B5E2B] text-white flex items-center justify-center mx-auto mb-3 font-bold">
            {item.step}
          </div>
          <h3 className="font-bold text-[#2C3E50] mb-1" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>{item.title}</h3>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>{item.desc}</p>
        </div>
      ))}
    </div>
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
      <button onClick={onOpenForm} className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-lg transition-all hover:opacity-90 shadow-lg" style={{ background: '#6B5E2B' }}>
        <Send size={20} /> Get Your Free Playbook
      </button>
      <button onClick={onDownload} className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border-2 font-bold text-lg transition-all hover:bg-slate-50" style={{ borderColor: '#6B5E2B', color: '#6B5E2B' }}>
        <Download size={20} /> Download PDF Directly
      </button>
    </div>
  </motion.div>
);

const FreeOfferSection = () => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="p-8 rounded-lg" style={{ background: 'linear-gradient(135deg, #6B5E2B 0%, #8B7E3B 100%)' }}>
    <div className="text-center max-w-2xl mx-auto">
      <div className="inline-block px-4 py-1 rounded-full bg-white/20 text-white text-sm font-semibold mb-4">
        LIMITED TIME OFFER
      </div>
      <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
        Get a FREE 1-Pager Website
      </h2>
      <p className="text-xl text-white/90 mb-6" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
        Worth <strong>R3,997</strong> \u2014 with lifetime hosting, absolutely FREE
      </p>
      <div className="bg-white/10 rounded-lg p-6 mb-6 backdrop-blur-sm">
        <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>How It Works</h3>
        <div className="flex items-center justify-center gap-4 text-white/90 text-sm">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2 font-bold text-lg">1</div>
            <p>Refer 5 friends</p>
          </div>
          <ArrowRight className="w-6 h-6 text-white/60" />
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2 font-bold text-lg">2</div>
            <p>Quick questionnaire</p>
          </div>
          <ArrowRight className="w-6 h-6 text-white/60" />
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2 font-bold text-lg">3</div>
            <p>Website live in 24-48h</p>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

const LeadCaptureModal = ({ isOpen, onClose, onDownloadPdf }: { isOpen: boolean; onClose: () => void; onDownloadPdf: () => void }) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const validate = (): boolean => {
    const n: Partial<Record<keyof FormData, string>> = {};
    if (!formData.firstName.trim()) n.firstName = 'First name is required';
    if (!formData.email.trim()) n.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) n.email = 'Invalid email address';
    setErrors(n);
    return Object.keys(n).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setFormStatus('submitting');
    try {
      await new Promise((r) => setTimeout(r, 1500));
      console.log('CRM_Leads:', { ...formData, Source: 'Lead_Magnet', Status: 'Lead_Capture', ts: new Date().toISOString() });
      setFormStatus('success');
      setTimeout(() => { setFormStatus('idle'); setFormData(initialFormData); onDownloadPdf(); onClose(); }, 1500);
    } catch { setFormStatus('error'); }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#2C3E50]" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>Get Your Free Playbook</h2>
                <p className="text-sm text-slate-500 mt-1">Enter your details to download the playbook</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={20} className="text-slate-400" /></button>
            </div>
            {formStatus === 'success' ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-green-600" /></div>
                <h3 className="text-lg font-bold text-green-800 mb-2">You&rsquo;re In!</h3>
                <p className="text-slate-600">Your playbook download will start shortly.</p>
              </div>
            ) : formStatus === 'error' ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4"><AlertCircle className="w-8 h-8 text-red-600" /></div>
                <h3 className="text-lg font-bold text-red-800 mb-2">Something Went Wrong</h3>
                <p className="text-slate-600 mb-4">Please try again or contact support.</p>
                <button onClick={() => setFormStatus('idle')} className="px-6 py-2 rounded-lg bg-[#6B5E2B] text-white font-medium">Try Again</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">First Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border ${errors.firstName ? 'border-red-400' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-[#6B5E2B]/30 focus:border-[#6B5E2B]`} placeholder="Your first name" />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}</div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Email <span className="text-red-500">*</span></label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg border ${errors.email ? 'border-red-400' : 'border-slate-300'} focus:outline-none focus:ring-2 focus:ring-[#6B5E2B]/30 focus:border-[#6B5E2B]`} placeholder="your@email.com" />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}</div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                  <input type="text" value={formData.businessName} onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#6B5E2B]/30 focus:border-[#6B5E2B]" placeholder="Your business name (optional)" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Current Stage</label>
                  <select value={formData.currentStage} onChange={(e) => setFormData({ ...formData, currentStage: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#6B5E2B]/30 focus:border-[#6B5E2B]">
                    <option value="">Select your stage (optional)</option>
                    {STAGES.map((s) => (<option key={s} value={s}>{s}</option>))}</select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Biggest Challenge</label>
                  <textarea value={formData.biggestChallenge} onChange={(e) => setFormData({ ...formData, biggestChallenge: e.target.value })}
                    rows={3} className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#6B5E2B]/30 focus:border-[#6B5E2B] resize-none"
                    placeholder="What&rsquo;s your biggest business challenge right now? (optional)" /></div>
                <button type="submit" disabled={formStatus === 'submitting'}
                  className="w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2 disabled:opacity-70" style={{ background: '#6B5E2B' }}>
                  {formStatus === 'submitting' ? (<><Loader2 size={18} className="animate-spin" /> Submitting...</>) : (<><Send size={18} /> Get My Free Playbook</>)}</button>
                <p className="text-xs text-slate-400 text-center">By submitting, you agree to receive emails from Chrysalinks Group. Unsubscribe anytime.</p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BackCover = () => (
  <div className="relative overflow-hidden rounded-lg p-12 text-center" style={{ background: 'linear-gradient(135deg, #2C3E50 0%, #1a1a2e 100%)' }}>
    <div className="relative">
      <div className="mb-6"><BookOpen className="w-12 h-12 mx-auto text-[#6B5E2B]" /></div>
      <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>Chrysalinks Group</h3>
      <p className="text-slate-300 mb-6">Helping lifestyle entrepreneurs 10x their revenues and customer bases.</p>
      <div className="space-y-2 text-slate-400 text-sm"><p>support@chrysalinks.co.za</p><p>+27 81 714 2228</p></div>
      <div className="mt-6 inline-block px-6 py-3 rounded-lg border border-[#6B5E2B] text-[#6B5E2B] font-medium text-sm">
        Scan QR Code &rarr; Get Your FREE 1-Pager Website
      </div>
    </div>
  </div>
);

export const LeadMagnetPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const generatePDF = useCallback(() => {
    setIsPdfGenerating(true);
    setTimeout(() => {
      try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pw = 210, ph = 297, m = 20;
        let y = m;
        const np = (need: number) => { if (y + need > ph - m) { pdf.addPage(); y = m; } };
        const div = () => { np(10); pdf.setDrawColor(107, 94, 43); pdf.setLineWidth(0.3); pdf.line(m, y, pw - m, y); y += 8; };

        pdf.setFillColor(26, 26, 46); pdf.rect(0, 0, pw, ph, 'F');
        pdf.setTextColor(255, 255, 255); pdf.setFontSize(28); pdf.setFont('times', 'bold');
        pdf.text('10x Your Revenue:', pw / 2, 80, { align: 'center' });
        pdf.setTextColor(107, 94, 43);
        pdf.text("The Lifestyle Entrepreneur's Playbook", pw / 2, 95, { align: 'center' });
        pdf.setTextColor(200, 200, 200); pdf.setFontSize(16); pdf.setFont('helvetica', 'normal');
        pdf.text('7 Laws to Transform Your Business from Side Hustle to Full-Time Income', pw / 2, 115, { align: 'center' });
        pdf.setDrawColor(107, 94, 43); pdf.setLineWidth(1); pdf.line(pw / 2 - 30, 130, pw / 2 + 30, 130);
        pdf.addPage(); y = m;

        pdf.setTextColor(107, 94, 43); pdf.setFontSize(11); pdf.setFont('helvetica', 'italic');
        pdf.text('"Helping lifestyle entrepreneurs 10x their revenues and customer bases."', m, y); y += 10; div();

        pdf.setFontSize(18); pdf.setFont('times', 'bold'); pdf.setTextColor(44, 62, 80);
        pdf.text('The 5 Stages of Entrepreneurial Growth', m, y); y += 12;
        const sd = [['Stage','Name','Customers','Revenue','Focus'],['1','BizInception','0-10','<R150K','Quit your day job'],['2','Full-Scale Launch','10-100','R150K-R10M','Product/Market Fit'],['3','Known Brand','100-1K','R10M-R100M','Client Monetization'],['4','Full Operation','1K-10K','R100M-R500M','Business Efficiency'],['5','Corporatising','10K+','R500M-R1B','Expansion/Scale']];
        const cw = [18, 35, 30, 30, 47], rh = 7;
        pdf.setFontSize(8); pdf.setFont('helvetica', 'bold');
        sd.forEach((row, ri) => {
          np(rh); let x = m;
          row.forEach((cell, ci) => {
            if (ri === 0) { pdf.setFillColor(107, 94, 43); pdf.setTextColor(255, 255, 255); }
            else { pdf.setFillColor(ri % 2 === 0 ? 248 : 255, ri % 2 === 0 ? 250 : 255, ri % 2 === 0 ? 252 : 255); pdf.setTextColor(44, 62, 80); }
            pdf.rect(x, y - 5, cw[ci], rh, 'F'); pdf.text(cell, x + 2, y); x += cw[ci];
          }); y += rh;
        }); y += 8; div();

        np(15); pdf.setFontSize(18); pdf.setFont('times', 'bold'); pdf.setTextColor(44, 62, 80);
        pdf.text('The 7 Laws of Success', m, y); y += 12;
        pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
        ['1. Goals & Plans - Set your Freedom Number','2. Education, Skills & Traits - Overcome skill gaps','3. Wellness (KPIs) - Track business health','4. Execution - The "10-a-Day Mantra"','5. Obstacle Overcoming - Translate frustrations into solutions','6. Performance Tracking - Daily/weekly metrics','7. Guidance & Oversight - Strategic leadership'].forEach(l => { np(8); pdf.text(l, m, y); y += 8; });
        y += 5; div();

        np(30); pdf.setFontSize(18); pdf.setFont('times', 'bold'); pdf.setTextColor(44, 62, 80);
        pdf.text('The Hotlist 100 Prospecting System', m, y); y += 12;
        pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
        ['The math: 15 conversations -> 5 qualified -> 3 sales','Build a curated list of 100 warm prospects.','Start today: list your first 10 prospects.'].forEach(l => { np(8); pdf.text(l, m, y); y += 8; });
        y += 8; div();

        np(30); pdf.setFontSize(18); pdf.setFont('times', 'bold');
        pdf.text('The 10-a-Day Mantra', m, y); y += 12;
        pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
        ['Mandatory 10 sales conversations daily.','Outreach = planting seeds; conversations = watering seeds.','Log your first 10 outreach messages.'].forEach(l => { np(8); pdf.text(l, m, y); y += 8; });
        y += 8; div();

        np(30); pdf.setFontSize(18); pdf.setFont('times', 'bold');
        pdf.text('Trust Builders & Social Proof', m, y); y += 12;
        pdf.setFontSize(11);
        ['Professional email: @yourdomain.com','Website: Your 1-pager (FREE via BizBuilder)','Testimonials: Ask every happy customer','Google Business Profile: Local SEO setup'].forEach(l => { np(8); pdf.text(l, m, y); y += 8; });
        y += 5; div();

        np(40); pdf.setFontSize(18); pdf.setFont('times', 'bold'); pdf.setTextColor(107, 94, 43);
        pdf.text('FREE Offer - 1-Pager Website', m, y); y += 12;
        pdf.setFontSize(12); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(44, 62, 80);
        pdf.text('Get a FREE 1-pager website (worth R3,997) with lifetime hosting.', m, y); y += 10;
        pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
        ['How it works: Refer 5 friends -> Get your website','24-48 hour delivery','Call to action: Start Now'].forEach(l => { np(8); pdf.text(l, m, y); y += 8; });
        y += 8; div();

        np(30); pdf.setFontSize(18); pdf.setFont('times', 'bold'); pdf.setTextColor(44, 62, 80);
        pdf.text('Next Steps', m, y); y += 12;
        pdf.setFontSize(11); pdf.setFont('helvetica', 'normal');
        ['1. Take the self-assessment - Identify your current stage','2. Join the BizBuilder Program (R197/mo self-paced)','3. Book a Discovery Call (for partner-delivered programs)'].forEach(l => { np(8); pdf.text(l, m, y); y += 8; });
        y += 10;

        np(50); pdf.setFillColor(44, 62, 80); pdf.rect(0, y - 20, pw, ph - y + 20, 'F');
        pdf.setTextColor(255, 255, 255); pdf.setFontSize(20); pdf.setFont('times', 'bold');
        pdf.text('Chrysalinks Group', pw / 2, y + 30, { align: 'center' });
        pdf.setFontSize(12); pdf.setFont('helvetica', 'normal');
        pdf.text('Helping lifestyle entrepreneurs 10x their revenues and customer bases.', pw / 2, y + 45, { align: 'center' });
        pdf.setFontSize(10);
        pdf.text('support@chrysalinks.co.za | +27 81 714 2228', pw / 2, y + 60, { align: 'center' });

        pdf.save('10x-Your-Your-Revenue-Lifestyle-Entrepreneurs-Playbook.pdf');
      } catch (err) {
        console.error('PDF generation error:', err);
      } finally {
        setIsPdfGenerating(false);
      }
    }, 500);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
      <div ref={contentRef} className="py-8 px-4 space-y-6 max-w-5xl mx-auto">
        <CoverPage /><SectionDivider />
        <WelcomeSection /><SectionDivider />
        <GrowthStagesTable /><SectionDivider />
        <SevenLawsSection /><SectionDivider />
        <HotlistSection /><SectionDivider />
        <TenADaySection /><SectionDivider />
        <TrustSection /><SectionDivider />
        <FreeOfferSection /><SectionDivider />
        <NextStepsSection onDownload={generatePDF} onOpenForm={() => setIsModalOpen(true)} /><SectionDivider />
        <BackCover />
      </div>
      <LeadCaptureModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onDownloadPdf={generatePDF} />
      {isPdfGenerating && (
        <div className="fixed bottom-6 right-6 bg-[#6B5E2B] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50">
          <Loader2 size={18} className="animate-spin" /><span className="font-medium">Generating PDF...</span>
        </div>
      )}
    </div>
  );
};
